import React, { ChangeEvent, useState } from "react"
import { IImageUpload, Image } from "./model"
import { ImageIcon } from "./image-icon"
import { RemoveIcon } from "./remove"
import { UploadIcon } from "./upload"
import { useTracking } from "react-tracking"
import { getImageTrackingValue } from "./functions"

import "./style.scss"

export function ImageUpload({
    id,
    style,
    disabled,
    className,
    label,
    tooltip,
    callback,
    defaultImage,
    type,
}: IImageUpload) {
    const [image, updateImage] = useState<Image>(defaultImage ?? {})

    async function loadImage(url: string) {
        return new Promise<HTMLImageElement>((resolve) => {
            const image = new Image()
            image.crossOrigin = "Anonymous"
            image.addEventListener("load", () => resolve(image))
            image.src = url
        })
    }

    function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
        event.preventDefault()

        if (!disabled) {
            const file = event.target.files ? event.target.files[0] : null

            if (file != null) {
                const blob = URL.createObjectURL(file)
                loadImage(blob).then((image) => {
                    const widthRatio = type === "logo" ? 200 : 100
                    let width = image.width
                    let height = image.height
                    switch (type) {
                        case "logo":
                            width = image.width > 100 ? 200 : image.width * 2
                            height = image.width > 100 ? image.height / (image.width / widthRatio) : image.height * 2
                            break
                        case "profile":
                            width = image.width > widthRatio ? widthRatio : image.width
                            height = image.width > widthRatio ? image.height / (image.width / widthRatio) : image.height
                            break
                        default:
                            width = image.width
                            height = image.height
                    }

                    const newImage = {
                        width: width,
                        height: height,
                        format: file.type,
                        name: file.name,
                        size: parseFloat((file.size / 1024).toFixed(1)),
                        url: blob,
                    }
                    updateImage(newImage)
                    callback(type, newImage)
                })
            }
        }
    }

    function removeImage() {
        if (!disabled) {
            updateImage({})
            callback(type, {})
        }
    }
    const { trackEvent } = useTracking()

    const track = (trackingLabel: string) => {
        const trackingValue = getImageTrackingValue(trackingLabel)

        trackEvent({
            event: "genericEvent",
            category: "click",
            action: "click image upload",
            label: `${trackingValue}`,
        })
    }

    return (
        <div className={`ini ${className || ""}`} style={style} id={`${id || ""}`}>
            {label && (
                <div className="ini-label">
                    {label}
                    {tooltip && <span>{tooltip}</span>}
                </div>
            )}
            {image.name ? (
                <div className={`ini-wrapper ${disabled ? "disabled" : ""}`} onClick={removeImage}>
                    <ImageIcon />
                    <div className="ini-content">
                        <div className="ini-title">{image !== {} && image.name}</div>
                        <div className="ini-instruction">{image !== {} && image.size} KB</div>
                        <div className="action-message">
                            <RemoveIcon />
                        </div>
                    </div>
                </div>
            ) : (
                <label className={`ini-wrapper ${disabled ? "disabled" : ""}`}>
                    <ImageIcon />
                    <div className="ini-content">
                        <div className="ini-title">Import file</div>
                        <div className="ini-instruction">File must be an image</div>
                        <div className="action-message">
                            <UploadIcon />
                        </div>
                    </div>
                    {!disabled && (
                        <input
                            id={`file-upload-${type}`}
                            type="file"
                            name={id}
                            accept="image/*"
                            onChange={handleFileChange}
                            onClick={() => track(type)}
                        />
                    )}
                </label>
            )}
        </div>
    )
}

export default ImageUpload
